import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { useMediaQuery } from '@mui/material';
import { ETFPageContainer } from 'components/layout';
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import BackgroundImage from '../../../assets/images/login_background_image.png';
import { AccessibilityLogo } from '../../../components/Logo/AccessibilityLogo';
import './Login.scss';
import { loginRequest } from '../api';

const formFields = {
    resetPassword: {
        username: {
            label: 'Email',
            placeholder: 'Enter your email',
        },
    },
};

const headerDescriptions: Record<string, string> = {
    default: 'Enter your authorized email below.',
    resetPassword:
        "Enter your email to receive a code for resetting your password. Check your junk or spam folder if you don't see it in your inbox.",
    confirmResetPassword: 'Please enter the code emailed to you to complete the password reset process.',
};

const MAX_RESENDS_PER_HOUR = 5;
const COOLDOWN_SECONDS = 30;
const LINK_EXPIRY_MS = 15 * 60 * 1000;
const EXPIRY_WARN_BEFORE_MS = 5 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

type Step = 'email' | 'confirm';

export function Login({ rememberMe: _rememberMe }: { rememberMe?: React.MutableRefObject<boolean | undefined> }) {
    const { route } = useAuthenticator((context) => [context.route]);
    const isMobile = useMediaQuery('(max-width:999.5px)');

    const [step, setStep] = useState<Step>('email');
    const [email, setEmail] = useState('');
    const [localError, setLocalError] = useState<string | null>(null);
    const [isPending, setIsPending] = useState(false);
    const confirmHeadingRef = useRef<HTMLHeadingElement>(null);

    const [resendTimestamps, setResendTimestamps] = useState<Record<string, number[]>>(() => {
        try {
            const stored = localStorage.getItem('cfra_auth_link_resend_timestamps');
            if (!stored) return {};
            const parsed = JSON.parse(stored) as Record<string, number[]>;
            const now = Date.now();
            const cleaned: Record<string, number[]> = {};
            for (const [key, timestamps] of Object.entries(parsed)) {
                const recent = timestamps.filter((t) => now - t < HOUR_MS);
                if (recent.length > 0) cleaned[key] = recent;
            }
            return cleaned;
        } catch {
            return {};
        }
    });
    const [cooldownSeconds, setCooldownSeconds] = useState(0);
    const [lastLinkIssuedAt, setLastLinkIssuedAt] = useState<number | null>(null);
    const [expiryWarningFired, setExpiryWarningFired] = useState(false);
    const [countdownMessage, setCountdownMessage] = useState('');
    const [expiryMessage, setExpiryMessage] = useState('');
    const [rateLimitMessage, setRateLimitMessage] = useState('');
    const [rateLimitEndTime, setRateLimitEndTime] = useState<number | null>(null);
    const description = headerDescriptions[route] || headerDescriptions.default;

    const useCustomForm = route === 'signIn' || route === 'idle' || route === 'setup';

    useEffect(() => {
        if (route !== 'signIn') {
            setLocalError(null);
        }
    }, [route]);

    useEffect(() => {
        if (step === 'confirm') {
            confirmHeadingRef.current?.focus();
        }
    }, [step]);

    useEffect(() => {
        localStorage.setItem('cfra_auth_link_resend_timestamps', JSON.stringify(resendTimestamps));
    }, [resendTimestamps]);

    useEffect(() => {
        if (cooldownSeconds <= 0) return;
        const timer = setTimeout(() => {
            const next = cooldownSeconds - 1;
            setCooldownSeconds(next);
            if (next <= 0) setCountdownMessage('');
        }, 1000);
        return () => clearTimeout(timer);
    }, [cooldownSeconds]);

    useEffect(() => {
        if (!lastLinkIssuedAt || expiryWarningFired) return;
        const warnAt = lastLinkIssuedAt + LINK_EXPIRY_MS - EXPIRY_WARN_BEFORE_MS;
        const delay = warnAt - Date.now();
        if (delay <= 0) return;
        const timer = setTimeout(() => {
            setExpiryMessage('Your sign-in link will expire in 5 minutes. You can request a new one below.');
            setExpiryWarningFired(true);
        }, delay);
        return () => clearTimeout(timer);
    }, [lastLinkIssuedAt, expiryWarningFired]);

    useEffect(() => {
        if (!rateLimitEndTime) return;
        const remaining = rateLimitEndTime - Date.now();
        if (remaining <= 0) {
            setRateLimitMessage('');
            setRateLimitEndTime(null);
            return;
        }
        setRateLimitMessage('Too many attempts. Please try again in 1 hour.');
        const timer = setTimeout(() => {
            setRateLimitMessage('');
            setRateLimitEndTime(null);
        }, remaining);
        return () => clearTimeout(timer);
    }, [rateLimitEndTime]);

    useEffect(() => {
        const emailKey = email.trim().toLowerCase();
        const recent = (resendTimestamps[emailKey] || []).filter((t) => Date.now() - t < HOUR_MS);
        if (recent.length < MAX_RESENDS_PER_HOUR) return;
        const oldest = Math.min(...recent);
        const delay = oldest + HOUR_MS - Date.now();
        const timer = setTimeout(() => {
            setResendTimestamps((prev) => ({
                ...prev,
                [emailKey]: (prev[emailKey] || []).filter((t) => Date.now() - t < HOUR_MS),
            }));
            setRateLimitMessage('');
            setRateLimitEndTime(null);
        }, delay);
        return () => clearTimeout(timer);
    }, [resendTimestamps, email]);

    const handleEmailSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setLocalError(null);

        const now = Date.now();
        const emailKey = email.trim().toLowerCase();
        const recent = (resendTimestamps[emailKey] || []).filter((t) => now - t < HOUR_MS);
        if (recent.length >= MAX_RESENDS_PER_HOUR) {
            const oldest = Math.min(...recent);
            setRateLimitEndTime(oldest + HOUR_MS);
            setStep('confirm');
            return;
        }

        setIsPending(true);
        try {
            const result = await loginRequest(email.trim());
            if (!result.success) {
                setLocalError(
                    "We don't recognize that email address. Please contact your CFRA Administrator for access.",
                );
                return;
            }
            setLastLinkIssuedAt(Date.now());
            setExpiryWarningFired(false);
            setExpiryMessage('');
            setStep('confirm');
        } catch {
            setLocalError('Something went wrong. Please try again.');
        } finally {
            setIsPending(false);
        }
    };

    const handleResend = async () => {
        const now = Date.now();
        const emailKey = email.trim().toLowerCase();
        const recent = (resendTimestamps[emailKey] || []).filter((t) => now - t < HOUR_MS);

        if (recent.length >= MAX_RESENDS_PER_HOUR) {
            const oldest = Math.min(...recent);
            setRateLimitEndTime(oldest + HOUR_MS);
            return;
        }

        setIsPending(true);
        try {
            const result = await loginRequest(email.trim());
            if (!result.success) {
                setLocalError(
                    "We don't recognize that email address. Please contact your CFRA Administrator for access.",
                );
                return;
            }
            setResendTimestamps((prev) => ({ ...prev, [emailKey]: [...(prev[emailKey] || []), now] }));
            setLastLinkIssuedAt(now);
            setExpiryWarningFired(false);
            setExpiryMessage('');
            setCooldownSeconds(COOLDOWN_SECONDS);
            setCountdownMessage(`Link sent. You can resend in ${COOLDOWN_SECONDS} seconds.`);
            setRateLimitMessage('');
        } catch {
            setLocalError('Something went wrong. Please try again.');
        } finally {
            setIsPending(false);
        }
    };

    const components = useMemo(
        () => ({
            Header() {
                return <AccessibilityLogo description={description} />;
            },
        }),
        [description],
    );

    const displayedError = localError;

    const isValidEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());
    const resendDisabled = cooldownSeconds > 0 || !!rateLimitMessage;
    const politeContent = expiryMessage || countdownMessage;

    return (
        <ETFPageContainer
            containerStyles={{
                maxWidth: 'unset',
                backgroundColor: '#fff',
                minHeight: '500px',
            }}>
            <div style={{ display: 'flex', minHeight: '100vh', height: '100%', overflow: 'hidden' }}>
                <div
                    style={{
                        flex: isMobile ? '0 0 100%' : '0 0 50%',
                        backgroundImage: `url(${BackgroundImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                />
                <div style={{ position: 'absolute', left: '50%', right: isMobile ? '50%' : '0%', border: '50%' }}>
                    {useCustomForm ? (
                        <div data-amplify-authenticator=''>
                            <div data-amplify-container=''>
                                <div data-amplify-router=''>
                                    <div data-amplify-router-content='' style={{ flexDirection: 'column', gap: 12 }}>
                                        <AccessibilityLogo description={step !== 'confirm' ? description : ''} />
                                        {step === 'confirm' ? (
                                            <div data-amplify-form='' style={{ textAlign: 'center' }}>
                                                <h3
                                                    ref={confirmHeadingRef}
                                                    tabIndex={0}
                                                    className='amplify-heading amplify-heading--3'
                                                    style={{ marginBottom: 12 }}>
                                                    Check your inbox
                                                </h3>
                                                <p
                                                    style={{
                                                        fontSize: 14,
                                                        color: isMobile ? '#fff' : '#555',
                                                        marginBottom: 24,
                                                        maxWidth: 400,
                                                    }}>
                                                    We sent a sign-in link to <b>{email}</b>. Open the link in any
                                                    browser to continue.
                                                </p>
                                                <div
                                                    aria-live='assertive'
                                                    aria-atomic='true'
                                                    style={{
                                                        position: 'absolute',
                                                        width: 1,
                                                        height: 1,
                                                        overflow: 'hidden',
                                                        clip: 'rect(0,0,0,0)',
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                    {rateLimitMessage}
                                                </div>
                                                {rateLimitMessage && (
                                                    <div
                                                        aria-hidden='true'
                                                        style={{
                                                            fontSize: 13,
                                                            color: '#b00020',
                                                            background: '#fde8e8',
                                                            padding: '8px 12px',
                                                            borderRadius: 10,
                                                            marginBottom: 8,
                                                            maxWidth: 400,
                                                            width: '100%',
                                                            boxSizing: 'border-box',
                                                        }}>
                                                        {rateLimitMessage}
                                                    </div>
                                                )}

                                                <button
                                                    type='button'
                                                    className='forgot-password-link'
                                                    style={{ display: 'block', margin: '0 auto 8px' }}
                                                    disabled={resendDisabled}
                                                    onClick={handleResend}>
                                                    Resend the link
                                                </button>

                                                <div
                                                    aria-live='polite'
                                                    aria-atomic='true'
                                                    style={{
                                                        position: 'absolute',
                                                        width: 1,
                                                        height: 1,
                                                        overflow: 'hidden',
                                                        clip: 'rect(0,0,0,0)',
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                    {politeContent}
                                                </div>
                                                {politeContent && (
                                                    <p
                                                        aria-hidden='true'
                                                        style={{
                                                            fontSize: 13,
                                                            color: isMobile ? '#fff' : '#555',
                                                            marginBottom: 16,
                                                        }}>
                                                        {politeContent}
                                                    </p>
                                                )}

                                                <hr
                                                    style={{
                                                        maxWidth: 400,
                                                        width: '100%',
                                                        border: 'none',
                                                        borderTop: '1px solid #ddd',
                                                        marginBottom: 24,
                                                    }}
                                                />
                                                <button
                                                    type='button'
                                                    className='cancel-link'
                                                    onClick={() => {
                                                        setLocalError(null);
                                                        setCooldownSeconds(0);
                                                        setCountdownMessage('');
                                                        setExpiryMessage('');
                                                        setRateLimitMessage('');
                                                        setRateLimitEndTime(null);
                                                        setLastLinkIssuedAt(null);
                                                        setExpiryWarningFired(false);
                                                        setStep('email');
                                                    }}>
                                                    ← Use a different email
                                                </button>
                                            </div>
                                        ) : (
                                            <form data-amplify-form='' onSubmit={handleEmailSubmit} noValidate>
                                                <fieldset
                                                    className='amplify-flex amplify-field-group'
                                                    disabled={isPending}>
                                                    <input
                                                        key='email'
                                                        className='amplify-input amplify-input-email'
                                                        type='email'
                                                        name='username'
                                                        autoComplete='email'
                                                        placeholder='Enter Email'
                                                        value={email}
                                                        onChange={(e) => {
                                                            setEmail(e.target.value);
                                                            if (localError && isValidEmail(e.target.value)) {
                                                                setLocalError(null);
                                                            }
                                                        }}
                                                        onBlur={() => {
                                                            if (email.trim() && !isValidEmail(email)) {
                                                                setLocalError('Please enter a valid email address.');
                                                            }
                                                        }}
                                                        autoFocus
                                                        required
                                                        aria-describedby='login-error'
                                                        aria-invalid={!!displayedError}
                                                    />
                                                </fieldset>
                                                <div
                                                    id='login-error'
                                                    aria-live='assertive'
                                                    aria-atomic='true'
                                                    style={{
                                                        position: 'absolute',
                                                        width: 1,
                                                        height: 1,
                                                        padding: 0,
                                                        margin: -1,
                                                        overflow: 'hidden',
                                                        clip: 'rect(0,0,0,0)',
                                                        whiteSpace: 'nowrap',
                                                        border: 0,
                                                    }}>
                                                    {displayedError ?? ''}
                                                </div>
                                                {displayedError && (
                                                    <div
                                                        className='amplify-alert amplify-alert--error'
                                                        style={{
                                                            maxWidth: 400,
                                                            width: '100%',
                                                            marginTop: 8,
                                                            padding: '8px 12px',
                                                            background: '#fde8e8',
                                                            color: '#b00020',
                                                            boxSizing: 'border-box',
                                                        }}>
                                                        {displayedError}
                                                    </div>
                                                )}
                                                <button
                                                    type='submit'
                                                    className='amplify-button amplify-button--primary amplify-button--fullwidth'
                                                    disabled={isPending || !isValidEmail(email)}>
                                                    {isPending ? 'Sending link...' : 'Send me a sign in link'}
                                                </button>
                                            </form>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <Authenticator
                            formFields={formFields}
                            components={components}
                            hideSignUp={true}
                            loginMechanisms={['email']}
                        />
                    )}
                </div>
            </div>
        </ETFPageContainer>
    );
}
